import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, IconButton, Tooltip, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { X } from 'lucide-react';
import { sbg } from '../../theme/sbgTokens';

/* ------------------------------------------------------------------------ */
/* Sheet (book §11.14): inspect or edit a record without losing the list     */
/* ------------------------------------------------------------------------ */

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** Secondary line under the title (record type, status). */
  subtitle?: React.ReactNode;
  /** Sticky footer actions: ghost Cancel, then the primary action. */
  actions?: React.ReactNode;
  size?: 'md' | 'lg';
  children: React.ReactNode;
  bodySx?: SxProps<Theme>;
}

export const Sheet: React.FC<SheetProps> = ({ open, onClose, title, subtitle, actions, size = 'md', children, bodySx }) => {
  const titleId = React.useId();
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { 'aria-labelledby': titleId, role: 'dialog' } as object }}
      PaperProps={{ sx: { width: { xs: '100%', sm: size === 'lg' ? 640 : 480 }, height: '100%', display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr) auto' } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          pl: 3,
          pr: 2,
          py: 2,
          borderBottom: `1px solid ${sbg.border.subtle}`,
        }}
      >
        <Box sx={{ minWidth: 0, pt: 0.5 }}>
          <Typography id={titleId} variant="h3" component="h2" sx={{ color: sbg.text.primary }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body1" component="div" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Tooltip title="Close">
          <IconButton aria-label="Close" onClick={onClose}>
            <X />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ overflow: 'auto', p: 3, display: 'grid', gap: 2, alignContent: 'start', ...bodySx }}>{children}</Box>
      {actions && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', px: 3, py: 2, borderTop: `1px solid ${sbg.border.subtle}` }}>
          {actions}
        </Box>
      )}
    </Drawer>
  );
};

/* ------------------------------------------------------------------------ */
/* ConfirmDialog (book §11.14): names the object and the consequence         */
/* ------------------------------------------------------------------------ */

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** "Delete Maria Chen?" — name the object. */
  title: React.ReactNode;
  /** The consequence: "Their time-off history is removed from every report. This can't be undone." */
  body?: React.ReactNode;
  /** Repeat the verb and object: "Delete employee". Never "OK" or "Yes". */
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  /** Present participle while loading: "Deleting…". */
  loadingLabel?: string;
  /** Disable the confirm button for a reason other than loading (explain it in `body` or `children`). */
  confirmDisabled?: boolean;
  /** Focus the confirm button on open (fast keyboard queues). Defaults to focusing Cancel for destructive dialogs. */
  autoFocusConfirm?: boolean;
  children?: React.ReactNode;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
  loadingLabel,
  confirmDisabled = false,
  autoFocusConfirm = false,
  children,
}) => {
  const baseId = React.useId();
  const titleId = `${baseId}-title`;
  const bodyId = `${baseId}-body`;
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby={titleId}
      aria-describedby={body ? bodyId : undefined}
    >
      <DialogTitle id={titleId}>{title}</DialogTitle>
      {(body || children) && (
        <DialogContent>
          {body && (
            <Typography id={bodyId} variant="body1" sx={{ color: sbg.text.secondary }}>
              {body}
            </Typography>
          )}
          {children}
        </DialogContent>
      )}
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={loading} autoFocus={destructive && !autoFocusConfirm}>
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          onClick={onConfirm}
          disabled={loading || confirmDisabled}
          aria-busy={loading || undefined}
          autoFocus={autoFocusConfirm}
        >
          {loading ? loadingLabel ?? `${confirmLabel}…` : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
